<script setup lang="ts">
import { computed } from 'vue';
import { useHistorycalStore } from '@/stores/useHistorycalStore';
import { useI18n } from 'vue-i18n';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  BarElement,
  BarController
} from 'chart.js';
import { Chart } from 'vue-chartjs';
import { metricOptions, filterByMetric } from '@/functions/historical_data';

// Register Chart.js plugins
ChartJS.register(Title, Tooltip, Legend, BarController, BarElement, LineElement, PointElement, LinearScale, CategoryScale);

const historyStore = useHistorycalStore();
const { t } = useI18n();

const options = metricOptions(t);

// Couleurs pour chaque métrique
const metricColors: Record<string, { border: string; background: string }> = {
  temperature: { border: 'rgb(255, 99, 132)', background: 'rgb(255, 99, 132)' }, // Rouge
  ph: { border: 'rgb(54, 162, 235)', background: 'rgb(54, 162, 235)' }, // Bleu
};

// Fonction pour compter les outliers basés sur les min/max du store
const countOutliers = (metric: string, items: any[]): number => {
  const historyStore = useHistorycalStore();

  // Pas de filtrage si pas de données
  if (items.length === 0) {
    return 0;
  }

  const outliers = items.filter(item => {
    const v = item._value;

    // Vérification des seuils pour la température
    if (metric === 'temperature') {
      const isOutlier = v < historyStore.temperatureMin || v > historyStore.temperatureMax;
      if (isOutlier) {
      }
      return isOutlier;
    }

    // Vérification des seuils pour le pH
    if (metric === 'ph') {
      const isOutlier = v < historyStore.phMin || v > historyStore.phMax;
      if (isOutlier) {
      }
      return isOutlier;
    }

    // Pour les autres métriques, pas de filtrage d'outliers
    return false;
  }).length;

  // Calculer le pourcentage d'outliers
  const percentage = (outliers / items.length) * 100;
  return Number(percentage.toFixed(2)); // Arrondi à 2 décimales
};

// Processed chart data for outliers
const chartData = computed(() => {
  const historicalData = historyStore.historicalData?.results || [];
  if (!historicalData.length) {
    return { labels: [], datasets: [] };
  }

  const metrics = ['ph', 'temperature'];

  const dayMap: Map<string, Map<string, any[]>> = new Map();

  metrics.forEach(metric => {
    const filteredItems = filterByMetric(metric, historicalData);
    filteredItems.forEach(item => {
      const time = new Date(item._time);
      const dateStr = time.toISOString().slice(0, 10);
      if (!dayMap.has(dateStr)) {
        dayMap.set(dateStr, new Map());
      }
      const metricDayMap = dayMap.get(dateStr)!;
      if (!metricDayMap.has(metric)) {
        metricDayMap.set(metric, []);
      }
      metricDayMap.get(metric)!.push(item);
    });
  });

  const days = Array.from(dayMap.keys()).sort();

  const datasets = metrics.map(metric => {
    const data = days.map(day => {
      const items = dayMap.get(day)?.get(metric) || [];
      return countOutliers(metric, items);
    });
    return {
      label: `${options.find(opt => opt.value === metric)?.label || metric} (Outliers %)`,
      data,
      backgroundColor: metricColors[metric].background,
      borderColor: metricColors[metric].border,
      borderWidth: 1,
    };
  });

  return { labels: days, datasets };
});

// Chart options
const chartOptions = {
  responsive: true,
  plugins: {
    tooltip: {
      callbacks: {
        title: (tooltipItems: any) => {
          return `Jour: ${tooltipItems[0].label}`;
        },
        label: (tooltipItem: any) => {
          return `${tooltipItem.dataset.label}: ${tooltipItem.parsed.y}%`;
        },
      },
    },
    legend: {
      position: 'top' as const,
    },
  },
  scales: {
    x: {
      type: 'category' as const,
      title: {
        display: true,
        text: 'Jour (YYYY-MM-DD)',
      },
    },
    y: {
      type: 'linear' as const,
      beginAtZero: true,
      title: {
        display: true,
        text: 'Nombre de valeurs aberrantes',
      },
    },
  },
};
</script>

<template>
  <h3>Analyse des valeures abérantes</h3>
    <div v-if="historyStore.historicalData" class="chart-wrapper">
      <div v-if="!chartData.labels.length" class="no-data">
        {{ $t('noHistoricalData') }}
      </div>
      <div v-else class="w-full max-w-3xl mx-auto">
        <Chart :type="'bar'" :data="chartData" :options="chartOptions" />
      </div>
    </div>
</template>

<style scoped>
.chart-wrapper {
  padding: 20px;
}

.no-data {
  text-align: center;
  color: red;
  font-weight: bold;
}
</style>